import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { CertificateAlgorithm } from '@smartcharge/shared/index';
import { InstallCertificateType, InstallCertificateTypes } from 'src/app/lib/commands/types';

export type InstallCertificateMethod = 'backend' | 'certificate' | 'file';

export interface InstallCertificateResult {
  method: InstallCertificateMethod;
  algorithm?: CertificateAlgorithm;
  certificateType?: InstallCertificateType;
  certificate?: string;
  certificateFile?: File;
  fileName?: string;
}

@Component({
  selector: 'sc-install-certificate',
  templateUrl: './install-certificate.component.html',
  styleUrls: ['./install-certificate.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InstallCertificateComponent implements OnInit {
  installCertificateForm: FormGroup;
  certificateAlgorithms: CertificateAlgorithm[] = ['RSA', 'ECDSA'];
  installCertificateTypes = InstallCertificateTypes;
  fileToUpload: File | null = null;

  constructor(public dialogRef: MatDialogRef<InstallCertificateComponent>) {}

  ngOnInit() {
    this.installCertificateForm = new FormGroup({
      method: new FormControl<InstallCertificateMethod>('certificate', [Validators.required]),
      algorithm: new FormControl<CertificateAlgorithm>(null),
      certificateType: new FormControl<InstallCertificateType>(null),
      certificate: new FormControl('')
    });

    this.installCertificateForm.get('method').valueChanges.subscribe(() => {
      this.fileToUpload = null;
      this.installCertificateForm.get('certificate').setValue('');
      this.updateValidators();
    });
    this.updateValidators();
  }

  get method(): InstallCertificateMethod {
    return this.installCertificateForm.get('method').value;
  }

  // Only the fields relevant to the currently selected method must be filled in.
  updateValidators() {
    const algorithm = this.installCertificateForm.get('algorithm');
    const certificateType = this.installCertificateForm.get('certificateType');
    const certificate = this.installCertificateForm.get('certificate');

    algorithm.clearValidators();
    certificateType.clearValidators();
    certificate.clearValidators();

    if (this.method === 'backend') {
      algorithm.setValidators([Validators.required]);
    } else {
      certificateType.setValidators([Validators.required]);
      if (this.method === 'certificate') {
        certificate.setValidators([Validators.required]);
      }
    }

    algorithm.updateValueAndValidity({ emitEvent: false });
    certificateType.updateValueAndValidity({ emitEvent: false });
    certificate.updateValueAndValidity({ emitEvent: false });
  }

  handleFile(event: any) {
    const file: File = event.target?.files?.item(0);
    if (!file) return;

    this.fileToUpload = file;
  }

  handleOnSubmit() {
    if (this.method === 'file' && !this.fileToUpload) return;
    if (this.installCertificateForm.invalid) return;

    const value = this.installCertificateForm.value;
    const result: InstallCertificateResult = { method: this.method };

    if (this.method === 'backend') {
      result.algorithm = value.algorithm;
    } else {
      result.certificateType = value.certificateType;
      if (this.method === 'file') {
        result.certificateFile = this.fileToUpload;
        result.fileName = this.fileToUpload?.name;
      } else {
        result.certificate = value.certificate;
      }
    }

    this.dialogRef.close(result);
  }

  close() {
    this.dialogRef.close();
  }
}
